import { useEffect, useMemo } from 'react';

import createTagScript from 'helpers/createTagScript';
import LogService from 'services/LogService';
import {
  installPplCtaTrackingCapture,
  withEmbeddedEngagementComponent,
  withExitIntentEngagementComponent,
  withPplCtaTracking,
} from 'shared-utils/lasTracking';

const getUTMs = () => {
  if (!window.localStorage) {
    return;
  }

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const trackingParams = JSON.parse(
    localStorage.getItem('tracking_params') || '{}'
  );
  const landingPageUrl = trackingParams['landing_page_url'];
  const referrerUrl = trackingParams['referrer_url'];

  const hasUtmParams = (params) =>
    params.get('utm_source') ||
    params.get('utm_medium') ||
    params.get('utm_campaign');

  if (hasUtmParams(urlParams) || !landingPageUrl) {
    return;
  }

  const landingPageUrlQueryString = landingPageUrl.split('?')[1];
  const landingPageUrlParams = new URLSearchParams(landingPageUrlQueryString);
  if (hasUtmParams(landingPageUrlParams)) {
    return;
  }

  return {
    utmSource: referrerUrl
      ? referrerUrl.match(/:\/\/(?:www\.)?([^/]+)/i)[1]
      : 'direct',
    utmMedium: referrerUrl ? 'organic' : 'none',
  };
};

const withUtmTracking = (modalParams) => {
  const { utmMedium, utmSource } = getUTMs() || {};

  if (!utmMedium || !utmSource) {
    return modalParams;
  }

  if (
    modalParams.tracking?.includes('utm_medium=') &&
    modalParams.tracking?.includes('utm_source=')
  ) {
    return modalParams;
  }

  return {
    ...modalParams,
    tracking: [
      modalParams.tracking,
      `utm_medium=${utmMedium}&utm_source=${utmSource}`,
    ]
      .filter(Boolean)
      .join('&'),
  };
};

/**  @param {import("./types").default} props */
const useLasForm = ({
  hasPplExitIntentEnabled,
  global_category_id,
  config,
  form = 'addLasExitIntent',
  hasCallFunction = false,
  enabled = true,
  isPPL = false,
}) => {
  useEffect(() => {
    if (!enabled) return;
    createTagScript(
      'lead-acquisition-service',
      `https://lead-acquisition-service.getapp.com/las-form-min.js`
    );
    installPplCtaTrackingCapture();
  }, [enabled]);

  const lasModalParams = useMemo(
    () => ({
      brand: 'getapp',
      cGuid: global_category_id,
      ...config,
      isPPL,
    }),
    [global_category_id, config, isPPL]
  );

  useEffect(() => {
    if (!hasPplExitIntentEnabled) {
      return;
    }

    const modalParams = withExitIntentEngagementComponent(
      withUtmTracking(lasModalParams)
    );

    if (!hasCallFunction) {
      setTimeout(() => {
        try {
          window[form] && window[form](modalParams);
        } catch (error) {
          LogService.error(`Error in LAS ${form}`, error);
        }
      }, 800);
    }
  }, [
    hasPplExitIntentEnabled,
    global_category_id,
    form,
    config,
    hasCallFunction,
    lasModalParams,
  ]);

  // Return function to manually call the form with optional extra parameters
  return (extraModalProps = {}) => {
    try {
      const modalParams = withUtmTracking({
        ...lasModalParams,
        ...extraModalProps,
        isPPL,
      });

      window[form]?.(
        form === 'injectEmbeddedForm'
          ? withEmbeddedEngagementComponent(modalParams)
          : withPplCtaTracking(modalParams)
      );
    } catch (error) {
      LogService.error(`Error calling LAS ${form} function`, error);
    }
  };
};

export default useLasForm;
